import {Injectable} from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import {Constants} from "../constants";
import {forkJoin, Observable, of} from "rxjs";
import {environment} from "../../../environments/environment";
import {UsanaObject} from "../usana-object";
import {catchError, map, share, timeout} from "rxjs/operators";
import {LoggerService} from "../common/logger.service";
import {UsanaCookieService} from "../common/usana-cookie.service";
import {UrlService} from "../common/url.service";
import {SessionService} from "../common/session.service";
import {GqlCustomerService} from "../../checkout/graphql/customer/gql-customer.service";
import {Util} from "../util/util";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService extends UsanaObject {

    constructor(private http: HttpClient,
                private usanaCookieService: UsanaCookieService,
                private urlService: UrlService,
                private sessionService: SessionService,
                private gqlCustomerService: GqlCustomerService,
                loggerService: LoggerService) {
        super(loggerService);
    }

    /**
     * This is the old way of logging in via Portal
     *
     * @param username
     * @param password
     */
    public loginPortal(username: string, password: string): Observable<HttpResponse<any>> {
        const headers = {Authorization: 'Basic ' + btoa(username + ':' + password)};

        // the .pipe(share()) prevents the second subscription from calling the http.get again.
        // this endpoint is handling SSO tokens
        const loginRequest = this.http.get(Constants.URL_PORTAL_LOGIN, {headers, observe: 'response'}).pipe(share());
        loginRequest.subscribe(
            (response: HttpResponse<any>) => {
                this.log.debug("auth success: ", response.body);
                const requestAttrsReturned = response.body.requestAttrs;
                const yearDate = new Date();
                yearDate.setDate(yearDate.getDate() + 365);
                this.usanaCookieService.setCookie("i18n.cookie.locale", requestAttrsReturned.locale, yearDate);

            },
            (error: HttpErrorResponse) => {
                this.handleFailedLogin(error);
            }
        );
        return loginRequest;
    }


    /**
     * For most things we want to use the SSO front-end.  But we still do use the login for some things like
     * automatically logging people in after creating a PC, and possibly for China logins
     *
     * @param username
     * @param password
     */
    login(username: string, password: string): Observable<any> {
        // We do not want to mess with Baoying or Cbec at this time, they will not be using idToken any time soon
        if (Util.isBaoyingUrl() || Util.isCbecUrl()) {
            return this.loginPortal(username, password);
        }

        const url = `${environment.LOGIN_SERVICE_URL}/login/${Constants.USANA_LOGIN_REALM}/oidc`;
        return this.http.post(url, // this is hardcoded to usana realm for now
            {
                username: username,
                password: password,
                mobile: false,
                kiosk: false
            },
            {
                observe: 'response',
                withCredentials: true
            }
        ).pipe(map((loginResponse: HttpResponse<any>) => this.handleLoginResponse(loginResponse)));

        //We will want to do this in the not to distant future, once we get fully moved off secure token
        // return this.gqlCustomerService.loginUser(username, password).pipe(map((loginResponse: any) => loginResponse?.success));
    }

    handleLoginResponse(loginResponse: HttpResponse<any>): boolean {
        if (loginResponse.body) {
            if (!loginResponse.body.success) {
                this.log.error("Error logging in user, response said it failed");
            } else {
                return loginResponse.headers.has(Constants.SECURE_SSO_ID_TOKEN_COOKIE) || !Util.isEmpty(loginResponse.body.tokens?.idToken) || loginResponse.body.interrupts?.length > 0;
            }
        } else {
            this.log.error("Error logging in user, no response body returned");
        }
        throw loginResponse;
    }

    public handleFailedLogin(response: any) {
        this.log.debug("login error", response);
        const headers = response.headers;
        this.log.debug("login headers", headers);
        const reason = headers.get('Auth-Failure-Reason');
        this.log.debug("reason", reason);
        if (reason) {
            switch (reason) {
                case "credentials-expired":
                    window.location.href = "/mvc/auth/credentialsExpiredException";
                    break;
                default:
                    this.log.warn("auth error: ", response.error);
            }
        }
    }

    logout(): Observable<any> { //Should mirror what is going on in hub, mostly
        this.sessionService.clearSession();
        const shopLogout = this.http.get(this.urlService.buildLegacyUrl(Constants.URL_SHOP_LOGOUT), {withCredentials: true, responseType: 'text'})
            .pipe(
                timeout(3000),
                map((response: any) => {
                    this.log.debug("logout shop", response); return response;
                }),
                catchError((error) => {
                    this.log.error("logout error shop:", error); return of([]);
                })
            );

        //this endpoint is handling SSO tokens
        const portalLogout = this.http.get(Constants.URL_PORTAL_LOGOUT, {withCredentials: true, responseType: 'text'})
            .pipe(
                timeout(3000),
                map((response: any) => {
                    this.log.debug("logout portal", response); return response;
                }),
                catchError((error) => {
                    this.log.error("logout error portal:", error); return of([]);
                })
            );

        const cartLogout = this.http.get(this.urlService.buildCartUrl(Constants.URL_CART_LOGOUT))
            .pipe(
                timeout(3000),
                map((response: any) => {
                    this.log.debug("logout cart", response); return response;
                }),
                catchError((error) => {
                    this.log.error("logout error cart:", error); return of([]);
                })
            );

        return forkJoin(shopLogout, portalLogout, cartLogout);
    }
}
